/// <amd-module name="TFS/Admin/Groups/Pivots/MemberOf/Redux/MemberOfPivotSaga" />
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { filterXSS } from "xss";
import { xssStyleDefaultOptions } from "xss/PresetOptions";
import { IOperationStatus, Result } from "TFS/Admin/Common/Constants/Constants";
import { checkMembershipExistence, fetchGroupByDescriptor, listMembership, removeMembership } from "TFS/Admin/Common/DataAccessLayers/GraphDAL";
import { GraphClientName, IGraphRestClient } from "TFS/Admin/Common/RestClient/Graph";
import { IPermissionPage, MemberOfOperation, SearchStatus } from "TFS/Admin/Groups/Common/Constants";
import { PermissionsGraphGroup } from "TFS/Admin/Groups/Common/Contracts";
import { GroupsPivotActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsPivotActions";
import { MemberOfPivotActions, MemberOfPivotActionTypes } from "TFS/Admin/Groups/Common/Redux/Actions/MemberOfPivotActions";
import * as GroupsActionDataSource from "TFS/Admin/Groups/Common/Redux/DataSource/GroupsPivotPermissionsDataSource";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { getErrorMessage, IErrorAction } from "TFS/Admin/Groups/Common/Util/ErrorMessageHelper";
import { GroupsComponent, GroupsTelemetryLogger } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { getProjectName, getUrlForGraphSubject } from "TFS/Admin/Groups/Common/Util/LinkPropsHelper";
import { caseInsensitiveContains, format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { GraphGroup, GraphMembership } from "VSS/Platform/Contracts/Graph";
import { IVssLocationService } from "VSS/Platform/Location";
import { IIdentity } from "VSSUI/IdentityPicker";
import { MessageCardSeverity } from "VSSUI/MessageCard";

/**
 * Root permissions MemberOf saga.
 */
export function* memberOfSaga(): SagaIterator {
    // Register the initialize permissions handler
    yield effects.takeEvery(MemberOfPivotActionTypes.InitView, initializeMemberOfPivot);
    yield effects.takeLatest(MemberOfPivotActionTypes.SearchMembers, searchMembers);
    yield effects.takeEvery(MemberOfPivotActionTypes.DeleteDialogConfirm, deleteMembership);
    yield effects.takeEvery(MemberOfPivotActionTypes.SaveMemberOf, onSaveMemberOf);
    yield effects.takeEvery(MemberOfPivotActionTypes.MemberOfGroupPermissions, getGroupPermissions);
    yield effects.takeEvery(MemberOfPivotActionTypes.CheckMembershipExistence, onCheckMembershipExistence);
    yield effects.takeEvery(MemberOfPivotActionTypes.OnMultiDeleteClick, onMultiDeleteClick);
}

export function* initializeMemberOfPivot(action: ActionsOfType<MemberOfPivotActions, MemberOfPivotActionTypes.InitView>): SagaIterator {
    const { selectedGroup, selectedUser, permissionPage } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    var telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.MemberOfPivot, "Initialize member of pivot");
    yield effects.call(telemetryLogger.captureAttemptStart);

    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;

    var descriptor: string;

    try {
        const projectName = permissionPage == IPermissionPage.Project ? yield effects.call(getProjectName, pageContext, telemetryLogger) : undefined;

        if (selectedGroup) {
            descriptor = selectedGroup.descriptor;

            if (!selectedGroup.actionPermissions) {
                yield effects.put(GroupsPivotActions.getGroupPermissions(selectedGroup));
            }
        } else if (selectedUser) {
            descriptor = selectedUser.descriptor;
        } else {
            yield effects.call(telemetryLogger.captureError, "No selected group or user found");

            yield effects.put(MemberOfPivotActions.membersLoadFailed());
            return;
        }

        if (descriptor && descriptor.length > 0) {
            const members: GraphMembership[] = (yield effects.call(listMembership, descriptor, telemetryLogger)) as any;

            if (members && members.length > 0) {
                let groups: PermissionsGraphGroup[] = [];

                for (const member of members) {
                    const group: GraphGroup = (yield effects.call(
                        fetchGroupByDescriptor,
                        member.containerDescriptor,
                        telemetryLogger,
                        GroupsComponent.MemberOfPivot
                    )) as any;

                    let linkprops;
                    if (group.descriptor) {
                        const url = (yield effects.call(
                            getUrlForGraphSubject,
                            group.descriptor,
                            permissionPage,
                            locationService,
                            telemetryLogger,
                            projectName
                        )) as any;
                        linkprops = { href: url };
                    }

                    groups.push({ ...(group as PermissionsGraphGroup), linkProps: linkprops });
                    yield effects.put(MemberOfPivotActions.getMemberOfGroupPermissions(member.containerDescriptor, false));

                    yield effects.call(telemetryLogger.captureTelemetry, {
                        IsGroup: {
                            descriptor,
                            subjectKind: selectedGroup ? selectedGroup.subjectKind : selectedUser!.subjectKind,
                            membersOfCount: members.length
                        }
                    });
                }

                yield effects.put(MemberOfPivotActions.initialGroupFetch(groups));
            } else {
                yield effects.put(MemberOfPivotActions.membersLoadFailed());
            }
        } else {
            yield effects.call(telemetryLogger.captureTelemetry, {
                IsGroup: {
                    descriptor,
                    subjectKind: selectedGroup ? selectedGroup.subjectKind : selectedUser!.subjectKind
                },
                ErrorMessage: "No descriptor found."
            });

            yield effects.put(
                MemberOfPivotActions.memberOperationStatus(MemberOfOperation.Fetch, Resources.FetchMemberOfFailed, MessageCardSeverity.Error)
            );
            yield effects.put(MemberOfPivotActions.membersLoadFailed());
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
        yield effects.put(
            MemberOfPivotActions.memberOperationStatus(MemberOfOperation.Fetch, Resources.FetchMemberOfFailed, MessageCardSeverity.Error)
        );
        yield effects.put(MemberOfPivotActions.membersLoadFailed());
    }
}

export function* searchMembers(action: ActionsOfType<MemberOfPivotActions, MemberOfPivotActionTypes.SearchMembers>): SagaIterator {
    var { items, newKeyword, oldKeyword, permissionPage } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.MemberOfPivot, "Search MemberOf");
    yield effects.call(telemetryLogger.captureAttemptStart);

    if (!newKeyword) {
        // Reset view
        yield effects.put(MemberOfPivotActions.updateSearchStatus(SearchStatus.None));
    } else if (oldKeyword !== undefined && localeIgnoreCaseComparer(newKeyword, oldKeyword) !== 0) {
        // Search
        try {
            const projectName =
                permissionPage == IPermissionPage.Project ? yield effects.call(getProjectName, pageContext, telemetryLogger) : undefined;
            const resultUsers: PermissionsGraphGroup[] = [];
            for (const item of items.value) {
                if (caseInsensitiveContains(item.displayName, newKeyword)) {
                    let linkprops;
                    if (item.descriptor) {
                        const url = (yield effects.call(
                            getUrlForGraphSubject,
                            item.descriptor,
                            permissionPage,
                            locationService,
                            telemetryLogger,
                            projectName
                        )) as any;
                        linkprops = { href: url };
                    }

                    resultUsers.push({ ...item, linkProps: linkprops });
                }
            }
            yield effects.put(MemberOfPivotActions.updateSearchResult(newKeyword, resultUsers, false));
        } catch (error) {
            // Reset view
            yield effects.put(MemberOfPivotActions.updateSearchStatus(SearchStatus.None));

            yield effects.call(telemetryLogger.captureError, error.message || error);
        }
    } else {
        // Search did not change
        yield effects.put(MemberOfPivotActions.updateSearchStatus(SearchStatus.Completed));
    }
}

export function* deleteMembership(action: ActionsOfType<MemberOfPivotActions, MemberOfPivotActionTypes.DeleteDialogConfirm>): SagaIterator {
    var { member, parentGroup } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    var telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.MemberOfPivot, "Delete membership");
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        const group = (yield effects.call(removeMembership, member.descriptor, parentGroup.descriptor, telemetryLogger)) as any;

        yield effects.put(
            MemberOfPivotActions.memberOperationStatus(
                MemberOfOperation.Delete,
                format(
                    Resources.MemberOfDeleteSuccess,
                    filterXSS(member.displayName),
                    filterXSS(parentGroup.displayName)
                ),
                MessageCardSeverity.Info,
                [parentGroup]
            )
        );
        return group;
    } catch (error) {
        yield effects.put(
            MemberOfPivotActions.memberOperationStatus(
                MemberOfOperation.Delete,
                getErrorMessage(GroupsComponent.MemberOfPivot, error, IErrorAction.Delete),
                MessageCardSeverity.Error
            )
        );
        yield effects.call(telemetryLogger.captureError, error.message || error);
    }
}

export function* onSaveMemberOf(action: ActionsOfType<MemberOfPivotActions, MemberOfPivotActionTypes.SaveMemberOf>): SagaIterator {
    const { subjectIdentities, selectedMember, permissionPage } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");

    var addMemberTelemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
        pageContext,
        GroupsComponent.MemberOfPivot,
        "Add member to group"
    );
    yield effects.call(addMemberTelemetryLogger.captureAttemptStart);

    var multiOperationStatuses: IOperationStatus[] = [];
    var saveMembershipTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.MemberOfPivot, "Save membership");
    yield effects.call(saveMembershipTelemetryLogger.captureAttemptStart);

    try {
        const projectName =
            permissionPage == IPermissionPage.Project ? yield effects.call(getProjectName, pageContext, saveMembershipTelemetryLogger) : undefined;

        if (subjectIdentities.length === 0 || !selectedMember) {
            throw new Error("No data provided to add members to group");
        }
        const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;
        const success: PermissionsGraphGroup[] = [];
        const failed: { failedIdentity: IIdentity; errorMessage: string }[] = [];
        yield effects.all(
            subjectIdentities.map(subjectIdentity =>
                effects.call(function*() {
                    try {
                        yield effects.call([graphClient, graphClient.addMembership], selectedMember.descriptor, subjectIdentity.subjectDescriptor);
                        const container: GraphGroup = (yield effects.call(
                            fetchGroupByDescriptor,
                            subjectIdentity.subjectDescriptor,
                            addMemberTelemetryLogger
                        )) as any;

                        let linkprops;
                        if (container.descriptor) {
                            const url = (yield effects.call(
                                getUrlForGraphSubject,
                                container.descriptor,
                                permissionPage,
                                locationService,
                                saveMembershipTelemetryLogger,
                                projectName
                            )) as any;
                            linkprops = { href: url };
                        }

                        success.push({ ...(container as PermissionsGraphGroup), linkProps: linkprops });
                    } catch (error) {
                        failed.push({ failedIdentity: subjectIdentity, errorMessage: error.message });
                        yield effects.call(addMemberTelemetryLogger.captureError, error.message || error);
                    }
                })
            )
        );

        if (success) {
            for (var i = 0; i < success.length; i++) {
                multiOperationStatuses.push({
                    name: success[i].displayName,
                    value: Result.Success,
                    message: ""
                });
            }
        }

        if (failed) {
            for (var i = 0; i < failed.length; i++) {
                multiOperationStatuses.push({
                    name: failed[i].failedIdentity.displayName || "",
                    value: Result.Failed,
                    message: failed[i].errorMessage
                });
            }
        }

        let resultMessage: string;
        let cardSeverity: MessageCardSeverity;
        if (success.length === 0) {
            resultMessage = format(Resources.AddMemberOfFailed, failed.length);
            cardSeverity = MessageCardSeverity.Error;
        } else if (failed.length > 0) {
            resultMessage = format(Resources.AddMemberOfPartialSuccess, success.length, failed.length);
            cardSeverity = MessageCardSeverity.Warning;
        } else {
            resultMessage = format(Resources.AddMemberOfSuccess, success.length);
            cardSeverity = MessageCardSeverity.Info;
        }

        yield effects.put(
            MemberOfPivotActions.memberOperationStatus(MemberOfOperation.Add, resultMessage, cardSeverity, success, multiOperationStatuses)
        );
    } catch (error) {
        yield effects.put(
            MemberOfPivotActions.memberOperationStatus(
                MemberOfOperation.Add,
                getErrorMessage(GroupsComponent.MemberOfPivot, error, IErrorAction.Add),
                MessageCardSeverity.Error,
                [],
                multiOperationStatuses
            )
        );
        yield effects.call(saveMembershipTelemetryLogger.captureError, error.message || error);
    } finally {
        yield effects.put(MemberOfPivotActions.closeAddMemberOfPanel());
    }
}

export function* getGroupPermissions(action: ActionsOfType<MemberOfPivotActions, MemberOfPivotActionTypes.MemberOfGroupPermissions>): SagaIterator {
    const { subjectDescriptor, isSelectedGroup } = action.payload;

    if (!subjectDescriptor || subjectDescriptor.length === 0) {
        return;
    }
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    var telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.MemberOfPivot, "Fetch group permissions");
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        const permissions = (yield effects.call(GroupsActionDataSource.getGroupPermissions, pageContext, subjectDescriptor)) as any;
        if (permissions) {
            yield effects.put(MemberOfPivotActions.getMemberOfGroupPermissionsSuccess(permissions, isSelectedGroup, subjectDescriptor));
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
    }
}

export function* onCheckMembershipExistence(
    action: ActionsOfType<MemberOfPivotActions, MemberOfPivotActionTypes.CheckMembershipExistence>
): SagaIterator {
    var { member, parentGroup } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    var telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.MemberOfPivot, "Check membership");
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        if (parentGroup.subjectDescriptor) {
            let membershipExistence = (yield effects.call(checkMembershipExistence, member, parentGroup.subjectDescriptor, telemetryLogger)) as any;
            // If the membership already exist, we add the member to the invalid members list
            if (membershipExistence) {
                yield effects.put(MemberOfPivotActions.addInvalidMember(parentGroup));
            } else {
                yield effects.put(MemberOfPivotActions.addValidMember(parentGroup));
            }
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
    }
}

export function* onMultiDeleteClick(action: ActionsOfType<MemberOfPivotActions, MemberOfPivotActionTypes.OnMultiDeleteClick>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    var telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.MemberOfPivot, "Multi delete member of");
    yield effects.call(telemetryLogger.captureAttemptStart);

    var { member, parentsToDelete } = action.payload;
    var multiOperationStatuses: IOperationStatus[] = [];

    try {
        var countSuccessful: number = 0;
        var successfulParentDeletions: PermissionsGraphGroup[] = [];
        var countFailed: number = 0;

        var severity: MessageCardSeverity = MessageCardSeverity.Info;

        for (var i = 0; i < parentsToDelete.length; i++) {
            try {
                yield effects.call(removeMembership, member.descriptor, parentsToDelete[i].descriptor, telemetryLogger, GroupsComponent.GroupMembersPivot);
                countSuccessful++;
                successfulParentDeletions.push(parentsToDelete[i]);
                multiOperationStatuses.push({
                    name: parentsToDelete[i].displayName,
                    value: Result.Success,
                    message: ""
                });
            } catch (error) {
                countFailed++;
                multiOperationStatuses.push({
                    name: parentsToDelete[i].displayName,
                    value: Result.Failed,
                    message: error.message
                });
                yield effects.call(telemetryLogger.captureError, error.message || error);
            }
        }

        var multiDeleteResultMessage: string = "";
        if (countSuccessful > 0) {
            multiDeleteResultMessage += format(Resources.MultiDeleteParentSuccessMessage, countSuccessful);
        }

        if (countFailed > 0) {
            if (countSuccessful > 0) {
                multiDeleteResultMessage += Resources.And;
            }
            multiDeleteResultMessage += format(Resources.MultiDeleteParentFailureMessage, countFailed);
            severity = MessageCardSeverity.Error;
        }

        yield effects.put(
            MemberOfPivotActions.memberOperationStatus(
                MemberOfOperation.Delete,
                multiDeleteResultMessage,
                severity,
                successfulParentDeletions,
                multiOperationStatuses
            )
        );
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
        yield effects.put(
            MemberOfPivotActions.memberOperationStatus(MemberOfOperation.Delete, Resources.MultiDeleteParentsFailed, MessageCardSeverity.Error)
        );
    }
}
